import { FiMessageSquare } from 'react-icons/fi';

export const opinionEssayFrameworkData = {
  courseId: 'opinion-essay-framework',
  courseType: 'writing-course',
  title: 'Opinion Essay Framework',
  icon: FiMessageSquare,
  overview: {
    impact: 'Write well-structured agree/disagree essays that score Band 7+',
    focus: 'Thesis statements, PEEL paragraphs, counter-arguments, and strong conclusions',
    uses: 'IELTS Writing Task 2 — "To what extent do you agree or disagree?" questions',
    duration: '25-30 minutes'
  },
  sections: [
    {
      title: 'Essay Structure Overview',
      content: `<p>An IELTS opinion essay follows a clear four-paragraph structure. The examiner expects you to <strong>state your position clearly</strong> in the introduction and maintain it throughout.</p>
<p>The standard structure is:</p>
<ul>
  <li><strong>Introduction</strong> — Paraphrase the question + thesis statement (your opinion)</li>
  <li><strong>Body Paragraph 1</strong> — Your strongest supporting argument with evidence</li>
  <li><strong>Body Paragraph 2</strong> — Second supporting argument OR counter-argument + rebuttal</li>
  <li><strong>Conclusion</strong> — Restate your opinion and summarise key reasons</li>
</ul>
<p>The whole essay should be <strong>250-280 words</strong>. Spending 5 minutes planning before writing is essential.</p>`,
      points: [
        'Always paraphrase the question — never copy it word for word.',
        'Your thesis must clearly state your position (fully agree, partially agree, or disagree).',
        'Each body paragraph needs ONE clear main idea, not multiple.',
        'The conclusion should never introduce new arguments.'
      ]
    },
    {
      title: 'Writing Strong Thesis Statements',
      content: `<p>Your thesis statement is the single most important sentence in the essay. It tells the examiner <strong>exactly</strong> what you believe and sets up your entire argument.</p>
<p>A strong thesis has three qualities:</p>
<ol>
  <li><strong>Clear position</strong> — The reader knows immediately whether you agree, disagree, or partially agree.</li>
  <li><strong>Specific scope</strong> — It hints at the reasons you will develop in body paragraphs.</li>
  <li><strong>Academic tone</strong> — It avoids informal language like "I think" or "In my opinion" alone.</li>
</ol>
<p><strong>Weak:</strong> <em>"I think education is important."</em><br/>
<strong>Strong:</strong> <em>"I strongly believe that government investment in education yields greater long-term economic benefits than spending on defence, as it fosters innovation and reduces inequality."</em></p>`,
      points: [
        'Use hedging for partial agreement: "While X has some merit, I largely believe that…"',
        'Preview your main reasons in the thesis to create a roadmap for the reader.',
        'Avoid absolute language like "always" or "never" unless you can fully support it.',
        'Place your thesis as the last sentence of the introduction.'
      ]
    },
    {
      title: 'Developing Supporting Arguments (PEEL)',
      content: `<p>Each body paragraph should follow the <strong>PEEL</strong> structure to ensure your ideas are fully developed:</p>
<ul>
  <li><strong>P — Point:</strong> State your main argument in one clear sentence.</li>
  <li><strong>E — Explain:</strong> Elaborate on WHY this point matters or HOW it works.</li>
  <li><strong>E — Evidence/Example:</strong> Provide a specific example, statistic, or real-world reference.</li>
  <li><strong>L — Link:</strong> Connect back to your thesis or transition to the next paragraph.</li>
</ul>
<p><strong>Example PEEL paragraph:</strong></p>
<p><em><strong>(P)</strong> One compelling reason to invest in renewable energy is the long-term reduction in healthcare costs. <strong>(E)</strong> Fossil fuels release particulate matter and toxic gases that cause respiratory diseases, placing enormous strain on public health systems. <strong>(E)</strong> For instance, a 2022 WHO report estimated that air pollution from coal plants costs European nations over €50 billion annually in health expenditure. <strong>(L)</strong> Therefore, transitioning to clean energy is not merely an environmental decision but a fiscally responsible one.</em></p>`,
      points: [
        'Aim for 80-100 words per body paragraph — detailed but concise.',
        'Examples can be real-world, hypothetical, or from personal knowledge — all are accepted.',
        'The "Link" sentence is what distinguishes a Band 7 paragraph from a Band 6 one.',
        'Avoid listing multiple ideas — depth on one idea scores higher than breadth across many.'
      ]
    },
    {
      title: 'Addressing Counter-Arguments',
      content: `<p>Including a counter-argument demonstrates <strong>critical thinking</strong>, which examiners reward at Band 7+. You acknowledge the opposing view and then refute it.</p>
<p>There are two common approaches:</p>
<ol>
  <li><strong>Concession + Rebuttal</strong> — "While some argue that X, this overlooks the fact that Y."</li>
  <li><strong>Dedicated paragraph</strong> — Present the opposing view in Body 2, then refute it with stronger evidence.</li>
</ol>
<p><strong>Useful language for counter-arguments:</strong></p>
<ul>
  <li>"Admittedly, … However, …"</li>
  <li>"It could be argued that … Nevertheless, …"</li>
  <li>"Opponents of this view suggest that … Yet, this fails to account for …"</li>
  <li>"While there is some validity to the claim that …, the evidence overwhelmingly supports …"</li>
</ul>`,
      points: [
        'A counter-argument paragraph is optional — only include it if you can refute it convincingly.',
        'Always end the counter-argument section by reinforcing YOUR position.',
        'For "to what extent" questions, addressing the other side shows nuance.',
        'Keep the counter-argument shorter than your main supporting paragraphs.'
      ]
    }
  ],
  exercises: [
    {
      title: 'Identify Strong Thesis Statements',
      type: 'select',
      instructions: 'For each question, determine which thesis statement would score higher in an IELTS opinion essay.',
      questions: [
        {
          text: 'Which is the stronger thesis for the prompt: "Some people believe that university education should be free. To what extent do you agree?"',
          options: [
            'I agree that university should be free for everyone.',
            'I firmly believe that governments should subsidise university tuition, as an educated workforce drives economic growth and promotes social mobility.',
            'University education is very expensive and many people cannot afford it.'
          ],
          answer: 'I firmly believe that governments should subsidise university tuition, as an educated workforce drives economic growth and promotes social mobility.',
          explanation: 'This thesis clearly states a position (agree), previews two reasons (economic growth and social mobility), and uses academic language. The first option is too vague, and the third doesn\'t state a position at all.'
        },
        {
          text: 'Which thesis best demonstrates partial agreement for: "Technology has made our lives easier. Do you agree or disagree?"',
          options: [
            'Technology has both advantages and disadvantages.',
            'While technology has undeniably simplified daily tasks and communication, I believe its impact on mental health and social interaction warrants serious concern.',
            'In my opinion, technology is good because we use phones every day.'
          ],
          answer: 'While technology has undeniably simplified daily tasks and communication, I believe its impact on mental health and social interaction warrants serious concern.',
          explanation: 'This thesis acknowledges the positive side (concession) while clearly stating the writer\'s main concern. It previews specific issues (mental health, social interaction) that body paragraphs will develop.'
        },
        {
          text: 'What makes this thesis weak? "I think that children should learn a second language because it is good for them."',
          options: [
            'It uses "I think" which is too informal',
            'It is vague — "good for them" lacks specificity about what benefits will be discussed',
            'It is too short for an IELTS thesis',
            'It does not address a counter-argument'
          ],
          answer: 'It is vague — "good for them" lacks specificity about what benefits will be discussed',
          explanation: 'The main problem is vagueness. "Good for them" doesn\'t preview specific reasons. A stronger version: "I believe children should learn a second language, as bilingualism enhances cognitive flexibility and broadens career opportunities." Note: using "I think" is acceptable in IELTS.'
        },
        {
          text: 'For a "fully disagree" position, which opening phrase is most effective?',
          options: [
            'I completely disagree with the notion that…',
            'This essay will discuss why I disagree…',
            'I strongly contend that the opposite is true; namely, that…',
            'Some people think this but they are wrong.'
          ],
          answer: 'I strongly contend that the opposite is true; namely, that…',
          explanation: '"I strongly contend" is assertive yet academic. Option A is acceptable but less sophisticated. Option B is mechanical ("this essay will discuss…" is a Band 5-6 habit). Option D is too informal for academic writing.'
        }
      ]
    },
    {
      title: 'Match Arguments to Evidence',
      type: 'select',
      instructions: 'Select the most appropriate piece of evidence to support each argument in an opinion essay.',
      questions: [
        {
          text: 'Argument: "Investing in public transport reduces carbon emissions." Which evidence best supports this?',
          options: [
            'Many people enjoy driving their cars to work.',
            'A study by the European Environment Agency found that cities with extensive metro systems produce 40% fewer transport-related emissions per capita.',
            'Public transport is cheaper than owning a car.',
            'Some cities have built new bus routes recently.'
          ],
          answer: 'A study by the European Environment Agency found that cities with extensive metro systems produce 40% fewer transport-related emissions per capita.',
          explanation: 'This evidence directly supports the claim with a specific statistic from a credible source. The other options are either irrelevant (A), support a different argument (C), or too vague (D).'
        },
        {
          text: 'Argument: "Remote working improves employee productivity." Which evidence is strongest?',
          options: [
            'Workers like staying at home because they can sleep later.',
            'A Stanford two-year study of 16,000 employees showed a 13% performance increase among remote workers, attributed to fewer distractions and reduced commute fatigue.',
            'Some companies allow remote work now.',
            'Remote workers can do household chores during breaks.'
          ],
          answer: 'A Stanford two-year study of 16,000 employees showed a 13% performance increase among remote workers, attributed to fewer distractions and reduced commute fatigue.',
          explanation: 'Specific data (13% increase), named source (Stanford), large sample (16,000), and explained mechanism (fewer distractions) make this the strongest evidence. Always aim for this level of specificity.'
        },
        {
          text: 'What is wrong with this PEEL paragraph? "Schools should teach financial literacy. This is because money is important. For example, people need money to live. In conclusion, financial education is needed."',
          options: [
            'The Point is unclear',
            'The Explain and Evidence sections are too vague and circular — they do not add depth',
            'The Link uses "in conclusion" which should only appear at the end of an essay',
            'Both B and C'
          ],
          answer: 'Both B and C',
          explanation: 'The explanation ("money is important") just restates the obvious, the example ("people need money to live") adds no real evidence, and "in conclusion" should be reserved for the essay\'s final paragraph. The paragraph needs specific benefits and concrete examples.'
        }
      ]
    },
    {
      title: 'Complete the Opinion Essay Outline',
      type: 'fill-in',
      instructions: 'Fill in the missing element of each essay outline component. Type the answer exactly as shown.',
      questions: [
        {
          text: 'Introduction formula: Paraphrase the question → Background sentence → ___',
          answer: 'Thesis statement',
          explanation: 'The introduction ends with your thesis statement — a clear declaration of your position that previews your main arguments.'
        },
        {
          text: 'PEEL stands for: Point, Explain, Evidence, ___',
          answer: 'Link',
          explanation: 'The Link sentence connects your paragraph back to the thesis or transitions smoothly to the next body paragraph. It reinforces how your argument supports your overall position.'
        },
        {
          text: 'When addressing a counter-argument, the pattern is: Concession → ___',
          answer: 'Rebuttal',
          explanation: 'After conceding the opposing point ("While some argue…"), you refute it with a rebuttal ("However, this overlooks…"). The rebuttal must be stronger than the concession to maintain your position.'
        },
        {
          text: 'A conclusion should: restate your ___ and summarise the key reasons.',
          answer: 'opinion',
          explanation: 'The conclusion restates your opinion (position/thesis) using different words and briefly summarises the main reasons from your body paragraphs. Never add new information in the conclusion.'
        }
      ]
    },
    {
      title: 'Identify the Essay Question Type',
      type: 'select',
      instructions: 'Determine which of these IELTS Task 2 prompts requires an opinion essay (agree/disagree) response.',
      questions: [
        {
          text: 'Which prompt requires an opinion essay?',
          options: [
            'Discuss the advantages and disadvantages of social media use among teenagers.',
            'Some people believe that the best way to reduce crime is to give longer prison sentences. To what extent do you agree or disagree?',
            'Why do many people choose to live in cities? What problems does this cause?'
          ],
          answer: 'Some people believe that the best way to reduce crime is to give longer prison sentences. To what extent do you agree or disagree?',
          explanation: '"To what extent do you agree or disagree?" is the classic opinion essay prompt. The first is a discussion/advantage-disadvantage essay, and the third is a two-part question (causes + effects).'
        },
        {
          text: 'Which phrase in a prompt signals an opinion essay?',
          options: [
            'What are the causes and effects of…',
            'Do you agree or disagree?',
            'Discuss both views and give your opinion.',
            'What solutions can you suggest?'
          ],
          answer: 'Do you agree or disagree?',
          explanation: '"Do you agree or disagree?" and "To what extent do you agree or disagree?" require opinion essays. "Discuss both views" is a discussion essay. The others are cause-effect or problem-solution types.'
        },
        {
          text: '"Some people think children should start school at age four, while others believe seven is more appropriate. Discuss both views and give your opinion." — Is this an opinion essay?',
          options: [
            'Yes — you must give your opinion, so it is an opinion essay.',
            'No — it is a discussion essay because it asks you to present BOTH views before giving your opinion.',
            'No — it is a problem-solution essay.'
          ],
          answer: 'No — it is a discussion essay because it asks you to present BOTH views before giving your opinion.',
          explanation: 'Even though you give your opinion at the end, "discuss both views" requires balanced treatment of two perspectives first. An opinion essay only asks for YOUR position. The essay structures differ significantly.'
        }
      ]
    },
    {
      title: 'Write a Thesis Statement',
      type: 'writing',
      instructions: 'Practise writing thesis statements for opinion essay prompts.',
      questions: [
        {
          text: 'Write a thesis statement for: "Some people believe that governments should spend money on space exploration. To what extent do you agree or disagree?"',
          example: 'I firmly believe that government funding for space exploration is justified, as it drives technological innovation that benefits everyday life and ensures humanity\'s long-term survival beyond Earth.',
          tip: 'State your position clearly (agree/disagree/partially), then preview two specific reasons that your body paragraphs will develop.'
        },
        {
          text: 'Write a "partially agree" thesis for: "The rise of artificial intelligence will lead to mass unemployment. To what extent do you agree?"',
          example: 'While artificial intelligence will inevitably displace certain routine occupations, I believe that it will simultaneously generate new industries and roles, meaning the overall impact on employment depends on how effectively governments invest in workforce retraining.',
          tip: 'Use a concession clause ("While… / Although…") to acknowledge the other side, then state your main position. Preview the specific nuance you will explore.'
        },
        {
          text: 'Write a strong concluding sentence for an essay arguing that zoos should be banned.',
          example: 'In conclusion, despite their claimed educational value, zoos fundamentally compromise animal welfare and perpetuate the notion that confining sentient beings for human entertainment is acceptable — a view that modern society must move beyond.',
          tip: 'A strong conclusion restates your opinion with conviction, echoes your main reasons without repeating them word-for-word, and ends on a decisive note.'
        }
      ]
    }
  ],
  tips: [
    'Spend 5 minutes planning your essay structure before writing — a clear plan prevents mid-essay confusion.',
    'Use the same position throughout; examiners penalise essays that switch sides without clear justification.',
    'Aim for 270-280 words — slightly over 250 shows depth without wasting time.',
    'Memorise 3-4 counter-argument phrases (e.g., "Admittedly,… However,…") so they come naturally under pressure.'
  ],
  dailyPractice: [
    'Write one thesis statement per day for a random IELTS opinion prompt — aim for under 2 minutes.',
    'Practise expanding a single argument into a full PEEL paragraph in 8 minutes.',
    'Read Band 9 model essays and highlight the thesis, topic sentences, evidence, and link sentences.',
    'Time yourself writing a full 4-paragraph opinion essay in 40 minutes, then self-assess against the IELTS criteria.'
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Opinion Essay Framework course covering IELTS Task 2 agree/disagree essays. Teaches thesis statement construction, PEEL paragraph development, counter-argument strategies, and essay structure. Suitable for Band 6-8 candidates preparing for "To what extent do you agree or disagree?" questions.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...opinionEssayFrameworkData,
      title: opinionEssayFrameworkData.title + ' (Foundations)',
      overview: { ...opinionEssayFrameworkData.overview, duration: '15-20 minutes' },
      sections: opinionEssayFrameworkData.sections.slice(0, Math.max(2, Math.ceil(opinionEssayFrameworkData.sections.length / 2))),
      exercises: opinionEssayFrameworkData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...opinionEssayFrameworkData,
      title: opinionEssayFrameworkData.title + ' (Advanced)',
      overview: { ...opinionEssayFrameworkData.overview, duration: '30-35 minutes' },
      sections: opinionEssayFrameworkData.sections,
      exercises: opinionEssayFrameworkData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(opinionEssayFrameworkData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return opinionEssayFrameworkData;
}
